import { useEffect, useState } from 'react'
import { Edit3, FlaskConical, Headphones, Image, Info, Plus, Radio, Square, Trash2 } from 'lucide-react'
import {
  getChannelCoverUrl,
  resolveMediaUrl,
  type Channel,
  type ChannelFilters,
  type StreamStatusResponse
} from '../../../api'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, Pill, RowActions, SearchField, ToggleSwitch } from '../../../components/ui'
import { ChannelMobileList } from '../ChannelMobileList'
import { ChannelStreamInlineControls } from '../ChannelStreamInlineControls'
import styles from './ChannelList.module.css'

type ChannelListProps = {
  channels: Channel[]
  filters: ChannelFilters
  onCreate: () => void
  onDelete: (channel: Channel) => Promise<void>
  onEdit: (channel: Channel) => void
  onFilter: (filters: ChannelFilters) => void
  onToggleStreamPlayback: (channel: Channel) => void
  onOpenPlayground: (channel: Channel) => void
  onStartStream: (channel: Channel) => Promise<void>
  onStopStream: (channel: Channel) => Promise<void>
  onToggleActive: (channel: Channel, active: boolean) => Promise<void>
  playingStreamChannelId: string | null
  startingStreamChannelId: string | null
  stoppingStreamChannelId: string | null
  streamPlaybackLoading: boolean
  streamPlaybackPlaying: boolean
  streamStatuses: Record<string, StreamStatusResponse | undefined>
  streamStatusesLoading: boolean
}

export const ChannelList = (props: ChannelListProps) => {
  const { channels, filters } = props
  const [search, setSearch] = useState(filters.search ?? '')
  const [openInfoChannelId, setOpenInfoChannelId] = useState<string | null>(null)

  useEffect(() => {
    if (!openInfoChannelId) {
      return undefined
    }

    const closeInfoPopover = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && target.closest('.channel-actions-cell, .channel-mobile-actions-cell')) {
        return
      }

      setOpenInfoChannelId(null)
    }

    document.addEventListener('pointerdown', closeInfoPopover)

    return () => {
      document.removeEventListener('pointerdown', closeInfoPopover)
    }
  }, [openInfoChannelId])

  const applyFilters = (nextSearch: string): void => {
    props.onFilter({
      search: nextSearch || undefined
    })
  }

  const handleSearchChange = (value: string): void => {
    setSearch(value)
    applyFilters(value)
  }

  return (
    <>
      <div className={styles.desktopToolbar}>
        <Button onClick={props.onCreate} variant="primary">
          <Plus size={16} /> Create channel
        </Button>
        <SearchField
          className={styles.searchField}
          onChange={(event) => handleSearchChange(event.target.value)}
          placeholder="Search channels..."
          value={search}
        />
      </div>

      <ChannelMobileList
        channels={channels}
        filters={filters}
        onDelete={props.onDelete}
        onEdit={props.onEdit}
        onToggleStreamPlayback={props.onToggleStreamPlayback}
        onOpenPlayground={props.onOpenPlayground}
        onStartStream={props.onStartStream}
        onStopStream={props.onStopStream}
        onToggleActive={props.onToggleActive}
        openInfoChannelId={openInfoChannelId}
        playingStreamChannelId={props.playingStreamChannelId}
        search={search}
        setOpenInfoChannelId={setOpenInfoChannelId}
        startingStreamChannelId={props.startingStreamChannelId}
        stoppingStreamChannelId={props.stoppingStreamChannelId}
        streamPlaybackLoading={props.streamPlaybackLoading}
        streamPlaybackPlaying={props.streamPlaybackPlaying}
        streamStatuses={props.streamStatuses}
        streamStatusesLoading={props.streamStatusesLoading}
        onSearchChange={handleSearchChange}
      />

      <DataTableSurface className={['channels-table-card', styles.desktopTable].join(' ')} fullWidth>
        <DataTableElement>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Active</th>
              <th>Visual Groups</th>
              <th>Audio Groups</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => {
              const vgCount = channel.visual_groups.length
              const agCount = channel.audio_groups.length
              const infoOpen = openInfoChannelId === channel.id
              const coverUrl = getChannelCoverUrl(channel, 'thumb')
              const streamStatus = props.streamStatuses[channel.id]
              const streamStatusKnown = Object.prototype.hasOwnProperty.call(props.streamStatuses, channel.id)
              const streamStatusLoading = props.streamStatusesLoading && !streamStatusKnown
              const streamStarting = props.startingStreamChannelId === channel.id
              const streamStopping = props.stoppingStreamChannelId === channel.id
              const isCurrentStreamPlayback = props.playingStreamChannelId === channel.id
              const streamListenLoading = isCurrentStreamPlayback && props.streamPlaybackLoading
              const streamListenPlaying = isCurrentStreamPlayback && props.streamPlaybackPlaying

              return (
                <tr key={channel.id}>
                  <td className="channel-title-cell" data-label="">
                    <div className={styles.titleRow}>
                      <ChannelStreamInlineControls
                        channel={channel}
                        loading={streamStatusLoading}
                        playbackLoading={streamListenLoading}
                        playbackPlaying={streamListenPlaying}
                        starting={streamStarting}
                        status={streamStatus}
                        stopping={streamStopping}
                        onStart={props.onStartStream}
                        onStop={props.onStopStream}
                        onTogglePlayback={props.onToggleStreamPlayback}
                      />
                      <button
                        className={styles.identityButton}
                        onClick={() => props.onEdit(channel)}
                        type="button"
                        aria-label={`Edit ${channel.name}`}
                      >
                        {coverUrl ? (
                          <img
                            className={styles.cover}
                            src={resolveMediaUrl(coverUrl)}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.coverPlaceholder}>
                            <Image size={16} />
                          </div>
                        )}
                        <div>
                          <div className={styles.nameLine}>
                            <span className={styles.name}>{channel.name}</span>
                          </div>
                          {channel.description && <div className={styles.description}>{channel.description}</div>}
                        </div>
                      </button>
                      {streamStatus?.status === 'error' && (
                        <span className={styles.streamErrorPill}>Stream error</span>
                      )}
                      </div>
                  </td>
                  <td className="channel-secondary-cell" data-label="Active">
                    <ToggleSwitch
                      checked={channel.is_active}
                      label={`Toggle ${channel.name} active state`}
                      onChange={(e) => void props.onToggleActive(channel, e.target.checked)}
                    />
                  </td>
                  <td className="channel-secondary-cell" data-label="Visual Groups">
                    <Pill size="md" tone={vgCount > 0 ? 'primary' : 'muted'}>
                      <Image size={11} />
                      {vgCount} group{vgCount !== 1 ? 's' : ''}
                    </Pill>
                  </td>
                  <td className="channel-secondary-cell" data-label="Audio Groups">
                    <Pill size="md" tone={agCount > 0 ? 'primary' : 'muted'}>
                      <Headphones size={11} />
                      {agCount} group{agCount !== 1 ? 's' : ''}
                    </Pill>
                  </td>
                  <td className="channel-actions-cell" data-label="Actions">
                    <RowActions className="channel-mobile-actions" mobileStack>
                      <IconButton
                        aria-expanded={infoOpen}
                        icon={<Info size={15} />}
                        label={`Channel info for ${channel.name}`}
                        onClick={() => setOpenInfoChannelId((current) => current === channel.id ? null : channel.id)}
                        title="Channel info"
                      />
                    </RowActions>
                    <RowActions className="channel-inline-actions" mobileStack style={{ justifyContent: 'flex-end' }}>
                      <IconButton
                        icon={<FlaskConical size={15} />}
                        label={`Open playground for ${channel.name}`}
                        onClick={() => props.onOpenPlayground(channel)}
                        title="Open playground"
                      />
                      <IconButton
                        icon={<Edit3 size={15} />}
                        label={`Edit ${channel.name}`}
                        onClick={() => props.onEdit(channel)}
                        title="Edit channel"
                      />
                      <IconButton
                        icon={<Trash2 size={15} />}
                        label={`Delete ${channel.name}`}
                        onClick={() => void props.onDelete(channel)}
                        title="Delete channel"
                        variant="danger"
                      />
                    </RowActions>
                    {infoOpen && (
                      <div className="track-info-popover channel-info-popover" role="dialog" aria-label={`Details for ${channel.name}`}>
                        <div>
                          <span>Active</span>
                          <ToggleSwitch
                            checked={channel.is_active}
                            label={`Toggle ${channel.name} active state`}
                            onChange={(e) => {
                              setOpenInfoChannelId(null)
                              void props.onToggleActive(channel, e.target.checked)
                            }}
                          />
                        </div>
                        <div>
                          <span>Visual groups</span>
                          <strong>{vgCount}</strong>
                        </div>
                        <div>
                          <span>Audio groups</span>
                          <strong>{agCount}</strong>
                        </div>
                        <div className="track-info-actions">
                          {streamStatus?.status === 'live' ? (
                            <IconButton
                              disabled={streamStopping}
                              icon={<Square size={15} />}
                              label={`Stop ${channel.name} stream`}
                              onClick={() => {
                                setOpenInfoChannelId(null)
                                void props.onStopStream(channel)
                              }}
                              title="Stop stream"
                              variant="danger"
                            />
                          ) : (
                            <IconButton
                              disabled={streamStarting}
                              icon={<Radio size={15} />}
                              label={`Start ${channel.name} stream`}
                              onClick={() => {
                                setOpenInfoChannelId(null)
                                void props.onStartStream(channel)
                              }}
                              title="Start stream"
                            />
                          )}
                          <IconButton
                            icon={<FlaskConical size={15} />}
                            label={`Open playground for ${channel.name}`}
                            onClick={() => {
                              setOpenInfoChannelId(null)
                              props.onOpenPlayground(channel)
                            }}
                            title="Open playground"
                          />
                          <IconButton
                            icon={<Edit3 size={15} />}
                            label={`Edit ${channel.name}`}
                            onClick={() => {
                              setOpenInfoChannelId(null)
                              props.onEdit(channel)
                            }}
                            title="Edit channel"
                          />
                          <IconButton
                            icon={<Trash2 size={15} />}
                            label={`Delete ${channel.name}`}
                            onClick={() => {
                              setOpenInfoChannelId(null)
                              void props.onDelete(channel)
                            }}
                            title="Delete channel"
                            variant="danger"
                          />
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </DataTableElement>
        {channels.length === 0 && (
          <EmptyState message="No channels match these filters." />
        )}
      </DataTableSurface>
    </>
  )
}
